export type ExperimentKind = 'Hooks' | 'Thumbnails' | 'Captions' | 'Timing' | 'Formats'

export type Experiment = {
    id: string
    kind: ExperimentKind
    title: string
    hypothesis: string
    a: { label: string, value: number }
    b: { label: string, value: number }
    metric: string
    verdict: 'Confirmed' | 'Busted' | 'Too close to call'
    learned: string
    sample: string
}

export const experimentKinds: Array<ExperimentKind> = ['Hooks', 'Thumbnails', 'Captions', 'Timing', 'Formats']

export const experiments: Array<Experiment> = [
    {
        id: 'L-014',
        kind: 'Hooks',
        title: 'Start at the end',
        hypothesis: 'Opening on the finished result will keep more people watching than opening on the first step.',
        a: { label: 'First step', value: 31 },
        b: { label: 'Finished result', value: 52 },
        metric: '% still watching at 5 seconds',
        verdict: 'Confirmed',
        learned: 'Show the payoff early. Patience comes after proof.',
        sample: '24 videos, 4 brands, 1.9M views'
    },
    {
        id: 'L-017',
        kind: 'Hooks',
        title: 'Face or no face',
        hypothesis: 'A human face in the first frame beats a product close-up for food content.',
        a: { label: 'Product only', value: 44 },
        b: { label: 'Face first', value: 47 },
        metric: '% still watching at 3 seconds',
        verdict: 'Too close to call',
        learned: 'For food, the food is the face. Keep both and test again.',
        sample: '18 videos, 2 brands, 640K views'
    },
    {
        id: 'L-021',
        kind: 'Thumbnails',
        title: 'Three words, not eight',
        hypothesis: 'Thumbnails with three words or fewer will earn more clicks than longer titles on the image.',
        a: { label: 'Eight words', value: 3.1 },
        b: { label: 'Three words', value: 5.4 },
        metric: 'click-through rate, %',
        verdict: 'Confirmed',
        learned: 'The thumbnail is a sign, not a paragraph.',
        sample: '30 videos, 3 channels, 2.6M impressions'
    },
    {
        id: 'L-023',
        kind: 'Thumbnails',
        title: 'Orange beats blue',
        hypothesis: 'A warm background on thumbnails will stand out more than a cool one in a busy feed.',
        a: { label: 'Cool blue', value: 4.6 },
        b: { label: 'Warm orange', value: 4.2 },
        metric: 'click-through rate, %',
        verdict: 'Busted',
        learned: 'Contrast with the feed matters more than our favourite colour. Annoying.',
        sample: '16 videos, 2 channels, 900K impressions'
    },
    {
        id: 'L-026',
        kind: 'Captions',
        title: 'Ask, do not tell',
        hypothesis: 'Ending a caption with a question people can answer in five words will double the replies.',
        a: { label: 'No question', value: 4.1 },
        b: { label: 'Short question', value: 9.3 },
        metric: 'replies per 1K views',
        verdict: 'Confirmed',
        learned: 'Make replying easy and people will. Make it homework and they will not.',
        sample: '42 posts, 5 brands'
    },
    {
        id: 'L-029',
        kind: 'Captions',
        title: 'The search caption',
        hypothesis: 'A first line written the way people search will bring more views from search than a clever line.',
        a: { label: 'Clever line', value: 12 },
        b: { label: 'Search line', value: 29 },
        metric: '% of views from search',
        verdict: 'Confirmed',
        learned: 'Write line one for the search bar. Save the joke for line two.',
        sample: '36 posts, 4 brands'
    },
    {
        id: 'L-031',
        kind: 'Timing',
        title: 'The quiet slot',
        hypothesis: 'Posting when competitors are silent beats posting at the busiest time for the audience.',
        a: { label: 'Peak hour', value: 18 },
        b: { label: 'Quiet slot', value: 24 },
        metric: 'first hour replies',
        verdict: 'Confirmed',
        learned: 'A smaller crowd that is paying attention beats a big one that is not.',
        sample: '28 posts, 3 brands'
    },
    {
        id: 'L-034',
        kind: 'Formats',
        title: 'Short video, long caption',
        hypothesis: 'Very short videos paired with longer, thoughtful captions will start more conversations.',
        a: { label: 'Short caption', value: 6.2 },
        b: { label: 'Long caption', value: 8.9 },
        metric: 'comments per 1K views',
        verdict: 'Confirmed',
        learned: 'The video stops the thumb. The caption gives people something to say.',
        sample: '22 posts, 3 brands'
    },
    {
        id: 'L-036',
        kind: 'Formats',
        title: 'Ten slides or five',
        hypothesis: 'Carousels with ten slides will earn more saves than carousels with five.',
        a: { label: 'Five slides', value: 3.4 },
        b: { label: 'Ten slides', value: 3.6 },
        metric: 'saves per 100 views',
        verdict: 'Too close to call',
        learned: 'Length is not the lever. A good slide two is.',
        sample: '20 carousels, 4 brands'
    }
]

export const labMethod = [
    { step: '01', title: 'Write the hunch', body: 'One sentence, written down before we start, so nobody can pretend they knew all along.' },
    { step: '02', title: 'Change one thing', body: 'Same video, same day, same budget. Only the hook, the thumbnail or the caption moves.' },
    { step: '03', title: 'Wait a full week', body: 'No peeking on day one. Early numbers lie, and they lie with great confidence.' },
    { step: '04', title: 'Publish the result', body: 'Wins, losses and the awkward middle. If it busts our favourite theory, it goes up anyway.' }
]

export const runningNow = [
    { id: 'L-038', title: 'Subtitles in yellow or white', progress: 0.64, daysLeft: 3 },
    { id: 'L-039', title: 'Founder voice-over versus music only', progress: 0.38, daysLeft: 5 },
    { id: 'L-040', title: 'Posting the same reel twice, a week apart', progress: 0.12, daysLeft: 9 }
]
